/**
 * Subscription Purchase Widget
 * Enhanced widget with purchase type selection, quantity control, and add to cart
 */

class SubscriptionPurchaseWidget {
  constructor(container) {
    this.container = container;
    this.sectionId = container.getAttribute("data-section-id");
    this.showOnlyWithSubscription =
      container.getAttribute("data-show-only-with-subscription") === "true";
    this.hiddenClass = "subscription-purchase-widget__variant-section--hidden";
    this.currentPurchaseType = "one-time";
    this.currentSellingPlanId = null;

    this.init();
  }

  init() {
    this.setupPurchaseTypeButtons();
    this.setupFrequencySelectors();
    this.listenToVariantChange();
    this.updateVariantDisplay();

    // Set initial state based on what's checked in the DOM
    this.setInitialState();
  }

  setInitialState() {
    const currentSection = this.currentVariantSection;
    if (!currentSection) return;

    // Find which button is checked by default
    const checkedButton = currentSection.querySelector(
      '.subscription-purchase-widget__radio[data-checked="true"]'
    );

    if (checkedButton) {
      const option = checkedButton.closest(
        ".subscription-purchase-widget__option"
      );
      if (option) {
        const purchaseType = option.getAttribute("data-purchase-type");
        const sellingPlanId = option.getAttribute("data-selling-plan-id");

        // Set the purchase type without re-rendering
        this.currentPurchaseType = purchaseType;
        this.currentSellingPlanId = sellingPlanId;
        this.updateSellingPlanInput();

        // Make sure delivery frequency is visible if subscription is selected
        if (purchaseType === "subscription") {
          this.toggleDeliveryFrequency(true);
        }
      }
    }
  }

  get shopifySection() {
    return document.querySelector(`#shopify-section-${this.sectionId}`);
  }

  get productForms() {
    if (!this.shopifySection) return [];
    return Array.from(
      this.shopifySection.querySelectorAll('form[action*="/cart/add"]')
    );
  }

  get variantIdInput() {
    for (const form of this.productForms) {
      const input =
        form.querySelector('input[name="id"]') ||
        form.querySelector('select[name="id"]');
      if (input) return input;
    }
    return null;
  }

  get currentVariantId() {
    if (!this.variantIdInput) return null;
    return this.variantIdInput.value;
  }

  get currentVariantSection() {
    if (!this.currentVariantId) return null;
    return this.container.querySelector(
      `[data-variant-id="${this.currentVariantId}"]`
    );
  }

  /**
   * Setup Purchase Type Selection (One-time vs Subscription)
   */
  setupPurchaseTypeButtons() {
    const buttons = this.container.querySelectorAll(
      ".subscription-purchase-widget__option"
    );

    buttons.forEach((button) => {
      button.addEventListener("click", (e) => {
        const purchaseType = button.getAttribute("data-purchase-type");
        const variantId = button.getAttribute("data-variant-id");
        const sellingPlanId = button.getAttribute("data-selling-plan-id");

        // Only update if it's for the current variant
        if (variantId === this.currentVariantId) {
          this.selectPurchaseType(purchaseType, sellingPlanId);
        }
      });
    });
  }

  selectPurchaseType(type, sellingPlanId = null) {
    this.currentPurchaseType = type;
    // If one-time is selected, clear the selling plan id
    if (type === "one-time") {
      this.currentSellingPlanId = null;
    } else {
      this.currentSellingPlanId = sellingPlanId;
    }

    // Update radio button states
    const currentSection = this.currentVariantSection;
    if (!currentSection) return;

    const buttons = currentSection.querySelectorAll(
      ".subscription-purchase-widget__option"
    );
    buttons.forEach((button) => {
      const btnType = button.getAttribute("data-purchase-type");
      const radio = button.querySelector(
        ".subscription-purchase-widget__radio"
      );

      if (btnType === type) {
        radio.setAttribute("data-checked", "true");
      } else {
        radio.setAttribute("data-checked", "false");
      }
    });

    // Update selling plan hidden input
    this.updateSellingPlanInput();

    // Show/hide delivery frequency
    this.toggleDeliveryFrequency(type === "subscription");

    // Dispatch custom event
    this.container.dispatchEvent(
      new CustomEvent("subscription:change", {
        bubbles: true,
        detail: {
          purchaseType: type,
          sellingPlanId: sellingPlanId,
          variantId: this.currentVariantId,
        },
      })
    );
  }

  /**
   * Setup Frequency Selector Dropdowns
   */
  setupFrequencySelectors() {
    const selectors = this.container.querySelectorAll(
      ".subscription-purchase-widget__frequency-select"
    );

    selectors.forEach((select) => {
      select.addEventListener("change", (e) => {
        const variantId = select.getAttribute("data-variant-id");
        const newSellingPlanId = select.value;

        // Only update if it's for the current variant
        if (variantId === this.currentVariantId) {
          this.currentSellingPlanId = newSellingPlanId;
          this.updateSellingPlanInput();

          // Update the button's data attribute
          const currentSection = this.currentVariantSection;
          if (currentSection) {
            const subscriptionButton = currentSection.querySelector(
              '.subscription-purchase-widget__option[data-purchase-type="subscription"]'
            );
            if (subscriptionButton) {
              subscriptionButton.setAttribute(
                "data-selling-plan-id",
                newSellingPlanId
              );
            }

            // Update discount badge and price
            this.updatePriceAndDiscount(variantId, newSellingPlanId);
          }
        }
      });
    });
  }

  /**
   * Update price and discount badge based on selected selling plan
   */
  updatePriceAndDiscount(variantId, sellingPlanId) {
    const currentSection = this.currentVariantSection;
    if (!currentSection) return;

    // Get selling plan data
    const dataScript = this.container.querySelector(
      `script[data-selling-plans="${variantId}"]`
    );
    if (!dataScript) return;

    try {
      const data = JSON.parse(dataScript.textContent);
      const selectedPlan = data.plans.find((plan) => plan.id == sellingPlanId);

      if (!selectedPlan) return;

      // Update discount badge
      const discountBadge = currentSection.querySelector(
        `.subscription-purchase-widget__discount-badge[data-discount-variant="${variantId}"]`
      );
      const discountValue = currentSection.querySelector(
        ".subscription-purchase-widget__discount-value"
      );

      if (discountBadge && discountValue) {
        if (selectedPlan.discount_percentage > 0) {
          discountValue.textContent = selectedPlan.discount_percentage;
          discountBadge.style.display = "";
        } else {
          discountBadge.style.display = "none";
        }
      }

      // Update price
      const priceElement = currentSection.querySelector(
        `.subscription-purchase-widget__option-price[data-price-variant="${variantId}"]`
      );
      if (priceElement && selectedPlan.price) {
        // Format price using Shopify money format
        const formattedPrice = this.formatMoney(selectedPlan.price);
        priceElement.textContent = formattedPrice;
      }

      // Update/show original price
      const originalPriceElement = currentSection.querySelector(
        `.subscription-purchase-widget__original-price[data-price-variant="${variantId}"]`
      );
      if (originalPriceElement) {
        if (selectedPlan.price < selectedPlan.compare_at_price) {
          originalPriceElement.style.display = "";
        } else {
          originalPriceElement.style.display = "none";
        }
      }
    } catch (error) {
      console.error("Error updating price and discount:", error);
    }
  }

  /**
   * Format money using Shopify's money format
   */
  formatMoney(cents) {
    // Get Shopify's money format from theme settings
    const moneyFormat = window.theme?.moneyFormat || "€{{amount}}";
    const amount = (cents / 100).toFixed(2).replace(".", ",");
    return moneyFormat.replace("{{amount}}", amount);
  }

  updateSellingPlanInput() {
    const currentSection = this.currentVariantSection;
    if (!currentSection) return;

    // Update all product forms with the selling plan
    this.productForms.forEach((form) => {
      let input = form.querySelector('input[name="selling_plan"]');
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = "selling_plan";
        form.appendChild(input);
      }
      // If one-time purchase, clear the value
      input.value = this.currentSellingPlanId || "";

      console.log("Updated selling plan input:", input.value);
    });
  }

  toggleDeliveryFrequency(show) {
    const currentSection = this.currentVariantSection;
    if (!currentSection) {
      return;
    }

    const frequency = currentSection.querySelector(
      `.subscription-purchase-widget__frequency[data-variant-id="${this.currentVariantId}"]`
    );

    if (frequency) {
      if (show) {
        frequency.style.display = "flex";
        frequency.classList.remove(this.hiddenClass);

        // Also remove hidden class from the select inside
        const select = frequency.querySelector(
          ".subscription-purchase-widget__frequency-select"
        );
        if (select) {
          select.classList.remove(this.hiddenClass);
        }
      } else {
        frequency.style.display = "none";
        frequency.classList.add(this.hiddenClass);

        // Also add hidden class to the select inside
        const select = frequency.querySelector(
          ".subscription-purchase-widget__frequency-select"
        );
        if (select) {
          select.classList.add(this.hiddenClass);
        }
      }
    }
  }

  /**
   * Variant Change Detection
   */
  listenToVariantChange() {
    if (!this.variantIdInput) return;

    // MutationObserver for variant changes
    const observer = new MutationObserver(() => {
      this.updateVariantDisplay();
    });

    observer.observe(this.variantIdInput, {
      attributes: true,
      attributeFilter: ["value"],
    });

    // Change event listener
    this.variantIdInput.addEventListener("change", () => {
      this.updateVariantDisplay();
    });

    // Custom variant change event
    if (this.shopifySection) {
      this.shopifySection.addEventListener("variant:change", () => {
        this.updateVariantDisplay();
      });
    }
  }

  updateVariantDisplay() {
    // Hide all variant sections
    const allSections = this.container.querySelectorAll("[data-variant-id]");
    allSections.forEach((section) => {
      section.classList.add(this.hiddenClass);
    });

    // Show current variant section
    const currentSection = this.currentVariantSection;
    if (currentSection) {
      currentSection.classList.remove(this.hiddenClass);

      // Check if variant has subscription options
      const hasSubscription =
        currentSection.querySelector('[data-purchase-type="subscription"]') !==
        null;

      // Hide entire widget if setting is enabled and no subscription available
      if (this.showOnlyWithSubscription && !hasSubscription) {
        this.container.style.display = "none";
        return;
      } else {
        this.container.style.display = "";
      }

      // Read the default selection from the DOM (set by Liquid based on settings)
      const defaultChecked = currentSection.querySelector(
        '.subscription-purchase-widget__radio[data-checked="true"]'
      );

      if (defaultChecked) {
        const option = defaultChecked.closest(
          ".subscription-purchase-widget__option"
        );
        if (option) {
          const purchaseType = option.getAttribute("data-purchase-type");
          const sellingPlanId = option.getAttribute("data-selling-plan-id");
          this.selectPurchaseType(purchaseType, sellingPlanId);
        }
      } else {
        // Fallback: Check what purchase types are available
        const subscriptionBtn = currentSection.querySelector(
          '[data-purchase-type="subscription"]'
        );
        const oneTimeBtn = currentSection.querySelector(
          '[data-purchase-type="one-time"]'
        );

        // Prefer subscription if available, otherwise one-time
        if (subscriptionBtn) {
          const sellingPlanId = subscriptionBtn.getAttribute(
            "data-selling-plan-id"
          );
          this.selectPurchaseType("subscription", sellingPlanId);
        } else if (oneTimeBtn) {
          this.selectPurchaseType("one-time", null);
        }
      }
    }
  }
}

// Initialize all subscription purchase widgets
function initSubscriptionPurchaseWidgets() {
  const widgets = document.querySelectorAll(".subscription-purchase-widget");
  widgets.forEach((widget) => {
    new SubscriptionPurchaseWidget(widget);
  });
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener(
    "DOMContentLoaded",
    initSubscriptionPurchaseWidgets
  );
} else {
  initSubscriptionPurchaseWidgets();
}

// Re-initialize for Shopify theme editor
document.addEventListener(
  "shopify:section:load",
  initSubscriptionPurchaseWidgets
);
document.addEventListener(
  "shopify:section:reorder",
  initSubscriptionPurchaseWidgets
);
